import { useState } from "react";
import { Bar, BarChart, Pie, PieChart, PieLabelRenderProps, XAxis, YAxis } from "recharts";
import { More } from "../../../components/MoreToggle";
import { Stats } from "../../../state/types";
import { KV, SubHeader } from "../util";

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  name,
}: PieLabelRenderProps) => {
  if (cx == null || cy == null || innerRadius == null || outerRadius == null) {
    return null;
  }
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = Number(cx) + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
  const y = Number(cy) + radius * Math.sin(-(midAngle ?? 0) * RADIAN);

  return (
    <>
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central">
        {name}
      </text>
      <text x={x} y={y + 20} fill="white" textAnchor="middle" dominantBaseline="central">
        {`${((percent ?? 1) * 100).toFixed(0)}%`}
      </text>
    </>
  );
};

export const PopulationDetails = ({ stats }: { stats: Record<string, number> }) => {
  const age = [
    { name: "0-15", value: stats.percentage_personen_0_tot_15_jaar },
    { name: "15-25", value: stats.percentage_personen_15_tot_25_jaar },
    { name: "25-45", value: stats.percentage_personen_25_tot_45_jaar },
    { name: "45-65", value: stats.percentage_personen_45_tot_65_jaar },
    { name: "65+", value: stats.percentage_personen_65_jaar_en_ouder },
  ];

  const houseHolds = [
    { name: "één persoon", value: stats.percentage_eenpersoonshuishoudens, fill: "#0088FE" },
    { name: "met kinderen", value: stats.percentage_huishoudens_met_kinderen, fill: "#00C49F" },
    {
      name: "zonder kinderen",
      value: stats.percentage_huishoudens_zonder_kinderen,
      fill: "#FFBB28",
    },
  ];

  const origin = [
    { name: "Nederland", value: stats.percentage_met_herkomstland_nederland, fill: "#0088FE" },
    {
      name: "Europa",
      value: stats.percentage_met_herkomstland_uit_europa_excl_nl,
      fill: "#00C49F",
    },
    { name: "Buiten EU", value: stats.percentage_met_herkomstland_buiten_europa, fill: "#FFBB28" },
  ];

  return (
    <div className="border-solid rounded-md p-2">
      <SubHeader>Leeftijdsverdeling</SubHeader>
      <BarChart style={{ width: "100%", height: 300 }} data={age}>
        <XAxis dataKey="name" />
        <YAxis width={20} />
        <Bar dataKey="value" fill="#8884d8" />
      </BarChart>
      <SubHeader>Huishoudens</SubHeader>
      <PieChart width="100%" height={300}>
        <Pie
          data={houseHolds}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          labelLine={false}
          isAnimationActive={false}
          label={renderCustomizedLabel}
        />
      </PieChart>
      <SubHeader>Herkomst</SubHeader>
      <PieChart width="100%" height={300}>
        <Pie
          data={origin}
          dataKey="value"
          nameKey="name"
          labelLine={false}
          isAnimationActive={false}
          label={renderCustomizedLabel}
        />
      </PieChart>
    </div>
  );
};

export const Bevolking = ({ stats }: { stats: Stats }) => {
  const [open, setOpen] = useState(false);
  const keywords = [];
  const bv = stats?.bevolkingsdichtheid_inwoners_per_km2;
  if (bv === undefined) return null;
  if (bv < 300) keywords.push("Dunbevolkt");
  if (bv > 8000) keywords.push("Dichtbevolkt");
  if (stats.percentage_met_herkomstland_nederland < 80) keywords.push("Internationaal");
  if (stats.percentage_met_herkomstland_nederland > 90) keywords.push("Nederlands");
  if (stats.percentage_personen_15_tot_25_jaar > 40) keywords.push("Jong");
  if (stats.percentage_personen_45_tot_65_jaar + stats.percentage_personen_65_jaar_en_ouder > 50)
    keywords.push("Oud");
  if (stats.percentage_eenpersoonshuishoudens > 45) keywords.push("Alleenwonend");
  if (stats.percentage_huishoudens_met_kinderen > 40) keywords.push("Kinderrijk");

  return (
    <>
      <KV k="Bevolking">
        <More open={open} setOpen={setOpen} />
        {keywords.join(". ") || "Gevarieerd"}.
      </KV>
      {open && <PopulationDetails stats={stats} />}
    </>
  );
};
